import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, StatusBadge } from '../../ui';
import { fetchCostEfficiency } from '../../../lib/api/analytics';
import { fetchEpics } from '../../../lib/api/epics';
import type { AnalyticsRange } from '../../../../../shared/types/analytics';
import type {
  CostEfficiencyResponse,
  EpicEfficiencyRecord,
} from '../../../../../shared/types/analytics';

// ---------------------------------------------------------------------------
// UX-02 — Efficiency tab (moved from pages/analytics/cost-efficiency.tsx).
// Range now comes from the hub's shared filter bar (`?since=`) instead of
// this page's own `?range=` selector; `sinceIsoFor` maps it to the ISO
// string the /analytics/cost-efficiency endpoint takes.
// ---------------------------------------------------------------------------

const RANGE_DAYS: Record<AnalyticsRange, number> = { '7d': 7, '30d': 30, '90d': 90, all: 3650 };

function sinceIsoFor(range: AnalyticsRange): string {
  const days = RANGE_DAYS[range] ?? RANGE_DAYS['90d'];
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

// Cost/SP colour thresholds (FR-6).
function cpsTone(cps: number): { tone: 'success' | 'warning' | 'danger'; hex: string } {
  if (cps <= 0.2) return { tone: 'success', hex: 'var(--cps-green, #22A06B)' };
  if (cps <= 0.4) return { tone: 'warning', hex: 'var(--cps-amber, #E2A100)' };
  return { tone: 'danger', hex: 'var(--cps-red, #E5484D)' };
}

const usd = (n: number): string => `$${n.toFixed(2)}`;
const usd3 = (n: number): string => `$${n.toFixed(3)}`;

function relativeDate(iso: string): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const days = Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

type SortKey = 'completed' | 'sp' | 'cost' | 'cps';

export const EfficiencyPanel: React.FC<{
  since: AnalyticsRange;
  project: string | undefined;
  setProject: (p: string | undefined) => void;
  account?: string;
}> = ({ since, project, setProject, account }) => {
  const [search, setSearch] = useSearchParams();
  const repo = search.get('repo') ?? '';
  const showLegacy = search.get('legacy') === '1';

  const setParam = (key: string, val: string | null) => {
    const next = new URLSearchParams(search);
    if (val) next.set(key, val);
    else next.delete(key);
    setSearch(next, { replace: true });
  };

  const sinceIso = useMemo(() => sinceIsoFor(since), [since]);

  // Primary data — refetched when the shared range or active account changes.
  // Project + repo filtering happens client-side (FR-8).
  const effQ = useQuery({
    queryKey: ['cost-efficiency', since, account],
    queryFn: () => fetchCostEfficiency({ since: sinceIso, limit: 500, account }),
  });

  // Legacy epics (no cost/SP) — only fetched when the toggle is on.
  const legacyQ = useQuery({
    queryKey: ['cost-efficiency-legacy'],
    queryFn: () => fetchEpics({ limit: 100 }),
    enabled: showLegacy,
  });

  const allRecords = effQ.data?.epics ?? [];

  // Build filter option lists from the data.
  const repoOptions = useMemo(
    () => Array.from(new Set(allRecords.map((e) => e.repo))).sort(),
    [allRecords],
  );

  // Client-side project/repo filter. (This tab's "project" filter is really
  // an org-prefix hint — see the note in the original cost-efficiency page:
  // records don't carry a project id, so we approximate via repo org, same
  // as before UX-02.)
  const filtered = useMemo(
    () =>
      allRecords.filter((e) => {
        if (repo && e.repo !== repo) return false;
        if (project && !e.repo.startsWith(`${project}/`) && e.repo.split('/')[0] !== project)
          return false;
        return true;
      }),
    [allRecords, project, repo],
  );

  // Recompute the summary over the *filtered* set so it always matches
  // what's on screen (the API summary covers the unfiltered window).
  const summary = useMemo(() => computeSummary(filtered), [filtered]);

  const legacyRows = useMemo<LegacyRow[]>(() => {
    if (!showLegacy || !legacyQ.data) return [];
    const known = new Set(allRecords.map((e) => e.pk));
    return legacyQ.data.data.epics
      .filter((e) => isCompletedState(e.state))
      .filter((e) => {
        const pk = `REPO#${e.repo}#EPIC#${e.number}`;
        if (known.has(pk)) return false;
        if (repo && e.repo !== repo) return false;
        if (project && e.repo.split('/')[0] !== project) return false;
        return true;
      })
      .map((e) => ({
        pk: `REPO#${e.repo}#EPIC#${e.number}`,
        repo: e.repo,
        epicId: e.number,
        title: e.title,
        completedAt: e.updatedAt,
        costUsd: e.totalCostUsd,
      }));
  }, [showLegacy, legacyQ.data, allRecords, repo, project]);

  return (
    <div data-testid="efficiency-panel" className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Cost efficiency</h2>
        <p className="text-xs text-slate-600 mt-0.5">
          How much each unit of complexity costs to build. Lower cost per story point means Forge is getting more efficient.
        </p>
      </div>

      <FilterBar
        repoOptions={repoOptions}
        project={project ?? ''}
        repo={repo}
        showLegacy={showLegacy}
        setProject={(v) => setProject(v || undefined)}
        setRepo={(v) => setParam('repo', v || null)}
        setShowLegacy={(v) => setParam('legacy', v ? '1' : null)}
      />

      {effQ.isLoading && <div className="text-sm text-slate-500">Loading cost efficiency…</div>}
      {effQ.isError && (
        <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
          {(effQ.error as Error).message}
        </div>
      )}

      {effQ.isSuccess && (
        <>
          <SummaryStrip summary={summary} />

          {filtered.length === 0 ? (
            <EmptyState
              testid="cost-efficiency-empty"
              icon="📉"
              title="No completed epics with story points yet"
              description="Once an epic finishes, Forge records its cost per story point and it shows up here."
            />
          ) : (
            <>
              <div className="bg-white border border-slate-200 rounded-lg shadow-card">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">Cost / story point over time</div>
                  <TrendBadge records={filtered} />
                </div>
                <div className="px-5 py-4">
                  <TrendChart records={filtered} />
                </div>
              </div>

              <EpicTable records={filtered} legacy={legacyRows} />
            </>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

const FilterBar: React.FC<{
  repoOptions: string[];
  project: string;
  repo: string;
  showLegacy: boolean;
  setProject: (v: string) => void;
  setRepo: (v: string) => void;
  setShowLegacy: (v: boolean) => void;
}> = ({ repoOptions, project, repo, showLegacy, setProject, setRepo, setShowLegacy }) => {
  const orgOptions = useMemo(
    () => Array.from(new Set(repoOptions.map((r) => r.split('/')[0]))).sort(),
    [repoOptions],
  );
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="text-xs text-slate-600 flex items-center gap-1.5">
        Org
        <select
          value={project}
          onChange={(e) => setProject(e.target.value)}
          data-testid="filter-project"
          className="border border-slate-300 rounded-md text-xs px-2 py-1.5 bg-white"
        >
          <option value="">All</option>
          {orgOptions.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </label>
      <label className="text-xs text-slate-600 flex items-center gap-1.5">
        Repo
        <select
          value={repo}
          onChange={(e) => setRepo(e.target.value)}
          data-testid="filter-repo"
          className="border border-slate-300 rounded-md text-xs px-2 py-1.5 bg-white font-mono"
        >
          <option value="">All</option>
          {repoOptions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </label>
      <label className="text-xs text-slate-600 flex items-center gap-1.5 ml-auto cursor-pointer">
        <input
          type="checkbox"
          checked={showLegacy}
          onChange={(e) => setShowLegacy(e.target.checked)}
          data-testid="toggle-legacy"
          className="rounded border-slate-300"
        />
        Show legacy epics
      </label>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Summary strip
// ---------------------------------------------------------------------------

interface SummaryShape {
  epicCount: number;
  totalStoryPoints: number;
  totalCostUsd: number;
  avgCostPerStoryPoint: number;
  medianCostPerStoryPoint: number;
}

const SummaryStrip: React.FC<{ summary: SummaryShape }> = ({ summary }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="summary-strip">
    <Stat label="Epics with SP data" value={String(summary.epicCount)} />
    <Stat label="Avg cost / SP" value={usd3(summary.avgCostPerStoryPoint)} />
    <Stat label="Median cost / SP" value={usd3(summary.medianCostPerStoryPoint)} />
    <Stat label="Total SP delivered" value={String(summary.totalStoryPoints)} />
    <Stat label="Total cost" value={usd(summary.totalCostUsd)} />
  </div>
);

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-card px-4 py-3">
    <div className="text-2xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
    <div className="text-xl font-semibold text-slate-900 mt-0.5 tabular-nums">{value}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Trend badge — median(last 30d) vs median(prior 30d)
// ---------------------------------------------------------------------------

const TrendBadge: React.FC<{ records: EpicEfficiencyRecord[] }> = ({ records }) => {
  const trend = useMemo(() => {
    const now = Date.now();
    const d30 = 30 * 24 * 60 * 60 * 1000;
    const recent: number[] = [];
    const prior: number[] = [];
    for (const r of records) {
      const t = new Date(r.completedAt).getTime();
      if (Number.isNaN(t)) continue;
      const age = now - t;
      if (age <= d30) recent.push(r.costPerStoryPoint);
      else if (age <= 2 * d30) prior.push(r.costPerStoryPoint);
    }
    if (recent.length === 0 || prior.length === 0) return null;
    const a = medianOf(recent);
    const b = medianOf(prior);
    if (b === 0) return null;
    const pct = Math.round(((a - b) / b) * 100);
    return { pct };
  }, [records]);

  if (!trend) return <span className="text-2xs text-slate-400">not enough data for trend</span>;
  const down = trend.pct < 0;
  return (
    <StatusBadge tone={down ? 'success' : trend.pct === 0 ? 'neutral' : 'danger'}>
      {down ? '↓' : '↑'} {Math.abs(trend.pct)}% last 30d
    </StatusBadge>
  );
};

// ---------------------------------------------------------------------------
// Trend chart — hand-rolled SVG (no chart lib)
// ---------------------------------------------------------------------------

const CHART_W = 720;
const CHART_H = 240;
const PAD = { top: 16, right: 16, bottom: 28, left: 44 };

const REPO_COLORS = [
  '#0052CC', '#22A06B', '#E2A100', '#8270DB', '#E5484D', '#1FA8C9', '#D9730D',
];

const TrendChart: React.FC<{ records: EpicEfficiencyRecord[] }> = ({ records }) => {
  const points = useMemo(
    () =>
      records
        .map((r) => ({ ...r, t: new Date(r.completedAt).getTime() }))
        .filter((r) => !Number.isNaN(r.t))
        .sort((a, b) => a.t - b.t),
    [records],
  );

  const byRepo = useMemo(() => {
    const m = new Map<string, typeof points>();
    for (const p of points) {
      const list = m.get(p.repo) ?? [];
      list.push(p);
      m.set(p.repo, list);
    }
    return m;
  }, [points]);

  if (points.length === 0) return null;

  const tMin = points[0].t;
  const tMax = points[points.length - 1].t;
  const yMax = Math.max(0.001, ...points.map((p) => p.costPerStoryPoint)) * 1.15;

  const innerW = CHART_W - PAD.left - PAD.right;
  const innerH = CHART_H - PAD.top - PAD.bottom;
  const xOf = (t: number) =>
    PAD.left + (tMax === tMin ? innerW / 2 : ((t - tMin) / (tMax - tMin)) * innerW);
  const yOf = (v: number) => PAD.top + innerH - (v / yMax) * innerH;

  // 3-epic moving average over all points (date order).
  const ma = movingAverage(points.map((p) => p.costPerStoryPoint), 3);
  const maPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(p.t).toFixed(1)} ${yOf(ma[i]).toFixed(1)}`)
    .join(' ');

  const repos = Array.from(byRepo.keys());
  const yTicks = 4;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="w-full"
        style={{ minWidth: 480 }}
        role="img"
        aria-label="Cost per story point trend chart"
      >
        {/* Y gridlines + labels */}
        {Array.from({ length: yTicks + 1 }).map((_, i) => {
          const v = (yMax / yTicks) * i;
          const y = yOf(v);
          return (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={CHART_W - PAD.right} y2={y} stroke="#E2E8F0" strokeWidth={1} />
              <text x={PAD.left - 6} y={y + 3} textAnchor="end" className="fill-slate-400" fontSize={10}>
                ${v.toFixed(2)}
              </text>
            </g>
          );
        })}

        {/* X end labels */}
        <text x={PAD.left} y={CHART_H - 8} textAnchor="start" className="fill-slate-400" fontSize={10}>
          {new Date(tMin).toLocaleDateString()}
        </text>
        <text x={CHART_W - PAD.right} y={CHART_H - 8} textAnchor="end" className="fill-slate-400" fontSize={10}>
          {new Date(tMax).toLocaleDateString()}
        </text>

        {/* One line per repo */}
        {repos.map((repoName, idx) => {
          const pts = byRepo.get(repoName)!;
          const color = REPO_COLORS[idx % REPO_COLORS.length];
          const path = pts
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(p.t).toFixed(1)} ${yOf(p.costPerStoryPoint).toFixed(1)}`)
            .join(' ');
          return (
            <g key={repoName}>
              {pts.length > 1 && <path d={path} fill="none" stroke={color} strokeWidth={1.5} />}
              {pts.map((p) => (
                <circle key={p.pk} cx={xOf(p.t)} cy={yOf(p.costPerStoryPoint)} r={3} fill={color}>
                  <title>{`${repoName} · epic #${p.epicId} · ${usd3(p.costPerStoryPoint)}/SP`}</title>
                </circle>
              ))}
            </g>
          );
        })}

        {/* 3-epic moving average (dotted) */}
        {points.length > 1 && (
          <path d={maPath} fill="none" stroke="#475569" strokeWidth={1.5} strokeDasharray="4 3" />
        )}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2 text-2xs text-slate-600">
        {repos.map((repoName, idx) => (
          <span key={repoName} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-0.5 rounded"
              style={{ backgroundColor: REPO_COLORS[idx % REPO_COLORS.length] }}
            />
            <span className="font-mono">{repoName}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 border-t-2 border-dashed border-slate-500" />
          3-epic moving avg
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Epic table
// ---------------------------------------------------------------------------

interface LegacyRow {
  pk: string;
  repo: string;
  epicId: number;
  title: string;
  completedAt: string;
  costUsd: number;
}

const SORT_KEYS: SortKey[] = ['completed', 'sp', 'cost', 'cps'];

const EpicTable: React.FC<{ records: EpicEfficiencyRecord[]; legacy: LegacyRow[] }> = ({
  records,
  legacy,
}) => {
  // Deep-linking: table sort lives in the URL (?sort=<key>:<asc|desc>) so a
  // shared link reproduces the same ordering. Defaults to completed:desc.
  const [searchParams, setSearchParams] = useSearchParams();
  const sort: { key: SortKey; dir: 'asc' | 'desc' } = (() => {
    const raw = searchParams.get('sort') ?? '';
    const [key, dir] = raw.split(':');
    if (SORT_KEYS.includes(key as SortKey) && (dir === 'asc' || dir === 'desc')) {
      return { key: key as SortKey, dir };
    }
    return { key: 'completed', dir: 'desc' };
  })();
  const setSort = (updater: (prev: { key: SortKey; dir: 'asc' | 'desc' }) => { key: SortKey; dir: 'asc' | 'desc' }) => {
    const next = updater(sort);
    const p = new URLSearchParams(searchParams);
    if (next.key === 'completed' && next.dir === 'desc') p.delete('sort');
    else p.set('sort', `${next.key}:${next.dir}`);
    setSearchParams(p, { replace: true });
  };

  const sorted = useMemo(() => {
    const copy = [...records];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case 'completed':
          cmp = a.completedAt.localeCompare(b.completedAt);
          break;
        case 'sp':
          cmp = a.totalStoryPoints - b.totalStoryPoints;
          break;
        case 'cost':
          cmp = a.epicTotalCostUsd - b.epicTotalCostUsd;
          break;
        case 'cps':
          cmp = a.costPerStoryPoint - b.costPerStoryPoint;
          break;
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [records, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden" data-testid="cost-efficiency-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            <Th>Epic</Th>
            <Th>Repo</Th>
            <Th sortable active={sort.key === 'completed'} dir={sort.dir} onClick={() => toggleSort('completed')}>
              Completed
            </Th>
            <Th sortable active={sort.key === 'sp'} dir={sort.dir} onClick={() => toggleSort('sp')} align="right">
              SP
            </Th>
            <Th sortable active={sort.key === 'cost'} dir={sort.dir} onClick={() => toggleSort('cost')} align="right">
              Cost
            </Th>
            <Th sortable active={sort.key === 'cps'} dir={sort.dir} onClick={() => toggleSort('cps')} align="right">
              Cost / SP
            </Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e) => {
            const tone = cpsTone(e.costPerStoryPoint);
            return (
              <tr key={e.pk} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                <td className="px-4 py-3">
                  <a
                    href={`https://github.com/${e.repo}/issues/${e.githubIssueId ?? e.epicId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    #{e.epicId} {e.title}
                  </a>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{e.repo}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{relativeDate(e.completedAt)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{e.totalStoryPoints}</td>
                <td className="px-4 py-3 text-right tabular-nums">{usd(e.epicTotalCostUsd)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold" style={{ color: tone.hex }}>
                  {usd3(e.costPerStoryPoint)}
                </td>
              </tr>
            );
          })}

          {legacy.map((e) => (
            <tr key={e.pk} className="border-b border-slate-100 last:border-b-0 bg-slate-50/40 text-slate-500">
              <td className="px-4 py-3">
                <a
                  href={`https://github.com/${e.repo}/issues/${e.epicId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  #{e.epicId} {e.title}
                </a>
                <StatusBadge tone="neutral">legacy</StatusBadge>
              </td>
              <td className="px-4 py-3 font-mono text-xs">{e.repo}</td>
              <td className="px-4 py-3 text-xs">{relativeDate(e.completedAt)}</td>
              <td className="px-4 py-3 text-right">—</td>
              <td className="px-4 py-3 text-right tabular-nums">{usd(e.costUsd)}</td>
              <td className="px-4 py-3 text-right">—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Th: React.FC<{
  children: React.ReactNode;
  sortable?: boolean;
  active?: boolean;
  dir?: 'asc' | 'desc';
  onClick?: () => void;
  align?: 'left' | 'right';
}> = ({ children, sortable, active, dir, onClick, align = 'left' }) => (
  <th
    className={
      'px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold ' +
      (align === 'right' ? 'text-right ' : '') +
      (sortable ? 'cursor-pointer select-none hover:text-slate-700' : '')
    }
    onClick={onClick}
  >
    {children}
    {sortable && active && <span className="ml-1">{dir === 'asc' ? '▲' : '▼'}</span>}
  </th>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function medianOf(values: number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

function movingAverage(values: number[], window: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - window + 1);
    const slice = values.slice(start, i + 1);
    out.push(slice.reduce((a, b) => a + b, 0) / slice.length);
  }
  return out;
}

function computeSummary(records: EpicEfficiencyRecord[]): SummaryShape {
  const cps = records.map((r) => r.costPerStoryPoint);
  return {
    epicCount: records.length,
    totalStoryPoints: records.reduce((a, r) => a + r.totalStoryPoints, 0),
    totalCostUsd: records.reduce((a, r) => a + r.epicTotalCostUsd, 0),
    avgCostPerStoryPoint: cps.length === 0 ? 0 : cps.reduce((a, b) => a + b, 0) / cps.length,
    medianCostPerStoryPoint: medianOf(cps),
  };
}

const COMPLETED_STATES = new Set([
  'COMPLETE',
  'READY_FOR_REVIEW',
  'READY_FOR_REVIEW_NOTIFIED',
  'BUGFIX_COMPLETE',
]);
function isCompletedState(state: string): boolean {
  return COMPLETED_STATES.has(state);
}

export default EfficiencyPanel;

// Keep the response type referenced so unused-import lint stays quiet if
// the panel is later split.
export type { CostEfficiencyResponse };
